import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InboxStackIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useParcels } from '@/hooks/useParcels'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import type { Parcel } from '@/types'

function getCarrierColor(name: string): string {
  const colors = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-violet-500', 'bg-cyan-500']
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return colors[Math.abs(hash) % colors.length]
}

function getInitials(name: string): string {
  return name.slice(0, 2).toUpperCase()
}

function ParcelCard({ parcel }: { parcel: Parcel }) {
  const { t } = useTranslation('parcels')
  const { formatDate } = useDateFormatter()
  const carrier = parcel.carrier || '??'
  const carrierColor = getCarrierColor(carrier)
  const carrierInitials = getInitials(carrier)

  return (
    <div className="flat-card">
      <div className="list-item-row">
        {/* Carrier avatar */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${carrierColor}`}>
          {carrierInitials}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium truncate">{carrier}</span>
            <span className={`text-xs shrink-0 ${parcel.status === 'pending' ? 'status-badge-warning' : 'status-badge-ghost'}`}>
              {parcel.status === 'pending' ? t('pending_tab') : t('collected_tab')}
            </span>
          </div>
          <p className="text-xs font-mono text-base-content/70">{parcel.tracking_number}</p>
          {parcel.description && (
            <p className="text-xs text-base-content/60 truncate">{parcel.description}</p>
          )}
          <p className="text-xs text-base-content/50">
            {parcel.status === 'collected' ? t('collected_at') : t('received_at')}
            {' '}
            {formatDate(parcel.status === 'collected' && parcel.collected_at ? parcel.collected_at : parcel.received_at)}
          </p>
        </div>
      </div>
    </div>
  )
}

export default function ParcelsPage() {
  const { t } = useTranslation('parcels')
  const { t: tc } = useTranslation('common')
  const [activeTab, setActiveTab] = useState<'pending' | 'collected'>('pending')
  const { data: parcels, isLoading, error } = useParcels(activeTab)

  return (
    <div className="p-4 space-y-3">
      {/* Pill-style tabs */}
      <div className="flex bg-base-200 rounded-full p-1 gap-1">
        <button
          className={`flex-1 py-1.5 text-sm font-medium rounded-full transition-all ${
            activeTab === 'pending'
              ? 'bg-base-100 text-base-content shadow-sm'
              : 'text-base-content/60'
          }`}
          onClick={() => setActiveTab('pending')}
        >
          {t('pending_tab')}
        </button>
        <button
          className={`flex-1 py-1.5 text-sm font-medium rounded-full transition-all ${
            activeTab === 'collected'
              ? 'bg-base-100 text-base-content shadow-sm'
              : 'text-base-content/60'
          }`}
          onClick={() => setActiveTab('collected')}
        >
          {t('collected_tab')}
        </button>
      </div>

      {isLoading && <LoadingSpinner />}

      {error && (
        <div className="alert alert-error">{tc('errors.something_went_wrong')}</div>
      )}

      {!isLoading && !error && parcels?.length === 0 && (
        <div className="text-center py-12 text-base-content/50">
          <div className="flex justify-center mb-3">
            <div className="icon-container">
              <InboxStackIcon className="w-5 h-5" />
            </div>
          </div>
          <p>{activeTab === 'pending' ? t('no_pending') : t('no_collected')}</p>
        </div>
      )}

      {parcels?.map((parcel) => (
        <ParcelCard key={parcel.id} parcel={parcel} />
      ))}
    </div>
  )
}
